// Generated commissions belong to received checks, independently of invoice issuance.
const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const dayOf = value => {
  if (typeof value !== 'string') return null;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!iso && !us) return null;
  const [year, month, day] = iso ? iso.slice(1).map(Number) : [Number(us[3]), Number(us[1]), Number(us[2])];
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
const monthOf = value => dayOf(value)?.slice(0, 7) || null;

// clientInvoices: facturas personalizadas a empresas; suman como ganancia de
// CSR (de su empresa/ajustador y del mes en que se pagaron) solo ya pagadas.
export function calculateAnalytics(records = [], checks = [], debts = [], clientInvoices = []) {
  const received = checks.filter(check => check.realValuesConfirmed || check.legado);
  const sum = (items, field) => items.reduce((total, item) => total + number(item[field]), 0);
  const groups = new Map();
  const months = new Map();
  const byParent = new Map();
  checks.forEach(check => {
    const children = byParent.get(check.claimRecordId) || [];
    children.push(check);
    byParent.set(check.claimRecordId, children);
  });
  const isCompleted = record => {
    const children = byParent.get(record.id) || [];
    return children.length > 0 && children.every(check => check.paid);
  };
  const completedClaims = records.filter(isCompleted).length;
  // Ganancia por empresa (hoy son dos y se quieren ver por separado).
  const companies = new Map();
  const getCompany = name => {
    const key = String(name || '').trim() || '—';
    if (!companies.has(key)) companies.set(key, { name: key, totalClaims: 0, completedClaims: 0, totalAmount: 0, csrCollected: 0, pending: 0, months: {} });
    return companies.get(key);
  };
  const addCompanyMonth = (company, monthKey, amount) => {
    if (monthKey) company.months[monthKey] = (company.months[monthKey] || 0) + amount;
  };
  records.forEach(record => {
    const company = getCompany(record.companyName);
    company.totalClaims++;
    if (isCompleted(record)) company.completedClaims++;
  });
  const getGroup = (name, company) => {
    const key = JSON.stringify([company || '', name || '']);
    if (!groups.has(key)) groups.set(key, { name: name || '—', company: company || '', totalClaims: 0, totalAmount: 0, adjusterCollected: 0, csrCollected: 0 });
    return groups.get(key);
  };
  records.forEach(record => { getGroup(record.adjusterName, record.companyName).totalClaims++; });
  let undatedCSR = 0;
  received.forEach(check => {
    const group = getGroup(check.adjusterName, check.companyName);
    group.totalAmount += number(check.amount);
    group.adjusterCollected += number(check.adjusterCollected);
    group.csrCollected += number(check.csrCollected);
    const monthKey = monthOf(check.checkDate);
    const company = getCompany(check.companyName);
    company.totalAmount += number(check.amount);
    company.csrCollected += number(check.csrCollected);
    company.pending += number(check.pending);
    addCompanyMonth(company, monthKey, number(check.csrCollected));
    if (monthKey) months.set(monthKey, (months.get(monthKey) || 0) + number(check.csrCollected));
    else undatedCSR += number(check.csrCollected);
  });
  const paidInvoices = clientInvoices.filter(invoice => invoice.paid);
  paidInvoices.forEach(invoice => {
    getGroup(invoice.adjusterName, invoice.companyName).csrCollected += number(invoice.amount);
    const monthKey = monthOf(invoice.paidDate);
    const company = getCompany(invoice.companyName);
    company.csrCollected += number(invoice.amount);
    addCompanyMonth(company, monthKey, number(invoice.amount));
    if (monthKey) months.set(monthKey, (months.get(monthKey) || 0) + number(invoice.amount));
    else undatedCSR += number(invoice.amount);
  });
  const clientInvoicesCollected = sum(paidInvoices, 'amount');
  const totalCSRCollected = sum(received, 'csrCollected') + clientInvoicesCollected;
  const totalOperatorDebt = sum(debts, 'amount');
  const totalOperatorDebtPaid = sum(debts.filter(debt => debt.paid), 'amount');
  return {
    totalClaims: records.length, totalChecks: checks.length, receivedChecks: received.length,
    completedClaims, pendingClaims: records.length - completedClaims,
    completionRate: records.length ? completedClaims / records.length * 100 : 0,
    totalAmount: sum(received, 'amount'), totalAdjusterCollected: sum(received, 'adjusterCollected'),
    totalCSRCollected, totalCollected: totalCSRCollected, clientInvoicesCollected,
    totalCSRPaid: sum(checks, 'deposit'), totalPending: sum(received, 'pending'),
    totalOperatorDebt, totalOperatorDebtPaid, totalOperatorDebtPending: totalOperatorDebt - totalOperatorDebtPaid,
    operatorDebtPerClaim: 5, realOperatorProfit: totalCSRCollected - totalOperatorDebt,
    adjusterStats: [...groups.values()], undatedCSR,
    companyStats: [...companies.values()].filter(company => company.totalClaims || company.csrCollected).sort((a, b) => a.name.localeCompare(b.name)),
    recentChecks: [...received].sort((a, b) => (dayOf(b.checkDate) || '').localeCompare(dayOf(a.checkDate) || '')).slice(0, 5)
      .map(check => ({ id: check.id, clientName: check.clientName, companyName: check.companyName, checkDate: dayOf(check.checkDate), csrCollected: number(check.csrCollected), amount: number(check.amount), paid: !!check.paid })),
    monthlyStats: [...months].sort(([a], [b]) => a.localeCompare(b)).map(([monthKey, amount]) => ({ monthKey, month: monthKey, amount }))
  };
}
